import { useEffect, useRef, useState } from 'react';
import { ImagePlus, Loader2, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch, inputCls } from '../../utils/operations';

/**
 * Shared Admin Media Library picker (image-only, search, upload, select).
 * Selection is local to the caller — nothing persists until the caller saves.
 * testidPrefix keeps per-module test ids unique (e.g. "rentacar-media", "transfer-media").
 */
export function MediaLibraryPicker({ ownerId, existing = [], onSelect, onClose, testidPrefix = 'media' }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [query, setQuery] = useState('');
  const fileRef = useRef(null);
  const load = async () => {
    setLoading(true);
    try {
      const data = await apiFetch('/files');
      setFiles((Array.isArray(data) ? data : []).filter((file) => (file.content_type || '').startsWith('image/')));
    } catch (err) {
      toast.error('Media Library indisponível', { description: String(err.message) });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const filtered = files.filter((file) => `${file.original_filename || ''} ${file.storage_path || ''}`.toLowerCase().includes(query.toLowerCase()));
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData(); body.append('file', file);
      const uploaded = await apiFetch('/upload', { method: 'POST', body });
      await load();
      onSelect(uploaded.url);
      toast.success('Imagem carregada e selecionada — guarda para confirmar');
    } catch (err) { toast.error('Upload falhou', { description: String(err.message) }); }
    finally { setUploading(false); }
  };
  return (
    <div role="dialog" aria-modal="true" aria-label="Media Library" data-testid={`${testidPrefix}-picker-${ownerId}`} className="fixed inset-0 z-[100] overflow-y-auto bg-eco-deep/80 p-4 backdrop-blur-sm">
      <div className="mx-auto max-w-5xl rounded-sm bg-eco-cream p-5 shadow-2xl md:p-7">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><h2 className="font-serif text-3xl font-semibold text-eco-ink">Media Library</h2><p className="mt-1 text-sm text-eco-ink/55">Selecionar uma imagem aprova-a apenas para este registo. Nada é guardado até clicares em Guardar.</p></div>
          <button type="button" data-testid={`${testidPrefix}-close-${ownerId}`} onClick={onClose} className="grid h-11 w-11 place-items-center rounded-full border border-black/10 bg-white"><X size={18} /></button>
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          <label className="relative min-w-64 flex-1"><Search size={15} className="absolute top-1/2 left-3 -translate-y-1/2 text-eco-ink/40" /><input data-testid={`${testidPrefix}-search-${ownerId}`} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Procurar por nome de ficheiro…" className={`${inputCls} !pl-9`} /></label>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid={`${testidPrefix}-upload-input-${ownerId}`} onChange={(event) => { upload(event.target.files?.[0]); event.target.value = ''; }} />
          <button type="button" data-testid={`${testidPrefix}-upload-${ownerId}`} onClick={() => fileRef.current?.click()} disabled={uploading} className="eco-btn-primary min-h-11 px-4 text-sm">{uploading ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />} Carregar imagem aprovada</button>
        </div>
        {loading ? <p className="py-16 text-center text-sm text-eco-ink/50"><Loader2 className="mr-2 inline animate-spin" size={16} /> A carregar Media Library…</p> : (
          <div className="mt-5 grid max-h-[55vh] grid-cols-2 gap-3 overflow-y-auto pr-1 md:grid-cols-4" data-testid={`${testidPrefix}-grid-${ownerId}`}>
            {filtered.map((file, index) => {
              const url = `/api/files/${file.storage_path}`;
              const selected = existing.includes(url);
              return (
                <button key={file.id || file.storage_path} type="button" data-testid={`${testidPrefix}-option-${ownerId}-${index}`} onClick={() => onSelect(url)} className={`overflow-hidden rounded-sm border bg-white text-left transition-colors ${selected ? 'border-eco-gold ring-2 ring-eco-gold/30' : 'border-black/10 hover:border-eco-gold'}`}>
                  <span className="block aspect-[4/3] bg-eco-ink/5"><img src={url} alt={file.original_filename || 'Media Library'} className="h-full w-full object-cover" loading="lazy" /></span>
                  <span className="block truncate p-2 text-[11px] font-semibold text-eco-ink/70">{file.original_filename || file.storage_path}</span>
                  {selected && <span className="block bg-eco-gold px-2 py-1 text-[10px] font-bold text-eco-deep">Selecionada</span>}
                </button>
              );
            })}
            {filtered.length === 0 && <p className="col-span-full rounded-sm border border-dashed border-black/15 bg-white px-4 py-12 text-center text-sm text-eco-ink/50" data-testid={`${testidPrefix}-empty-${ownerId}`}>Nenhuma imagem aprovada disponível nesta pesquisa.</p>}
          </div>
        )}
      </div>
    </div>
  );
}
